/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useRef} from 'react';
import {usePreferencesState} from '@ion/core/src/preferences/hooks/usePreferencesState';
import {Preference} from '@ion/core/src/types/preferences';

export interface UseGridPageSizeParams {
  usePreferedPageSize?: boolean;
  defaultPageSize?: number;
  setGridPageSize?: (pageSize: number) => void;
  preferenceKey?: Preference;
};

/**
 *
 * React Hook to provide utililty functions for saving grid Page Size in any Grid.
 *
 *  ```typescript
 *
 *   import {useGridPageSize} from '@/dls/src/grid';
 *
 *   const [preferredPageSize, setPreferredPageSize, preferredPageSize] = useGridPageSize();
 *
 *  ```
 */
export const useGridPageSize = ({
  setGridPageSize,
  defaultPageSize = 20,
  usePreferedPageSize = false,
  preferenceKey = Preference.gridRowsPerPage
}: UseGridPageSizeParams) => {
  const [pageSize, _setPageSize] = usePreferencesState(
    preferenceKey,
    defaultPageSize,
    usePreferedPageSize
  );
  const pageSizeRef = useRef(pageSize);

  const setPageSize = useCallback((size: number) => {
    _setPageSize(size, usePreferedPageSize);
    pageSizeRef.current = size;
    if (!usePreferedPageSize) {
      setGridPageSize?.(size);
    }
  }, [usePreferedPageSize, _setPageSize]);

  return [pageSize, setPageSize, pageSizeRef];
};
